import { useCallback, useEffect } from "react";

type NavigationItem = {
  id: string;
};

type RPControlHistoryState = {
  rpcontrolNavigation?: {
    scope: string;
    section: string;
  };
};

function historySection(state: unknown, scope: string, navigation: NavigationItem[], fallback: string) {
  const stored = state && typeof state === "object"
    ? (state as RPControlHistoryState).rpcontrolNavigation
    : undefined;
  const requested = stored?.scope === scope ? stored.section : fallback;
  return navigation.some((item) => item.id === requested) ? requested : fallback;
}

export function useNavigationHistory({
  scope,
  navigation,
  activeNavigation,
  defaultNavigation,
  onNavigate,
}: {
  scope: string;
  navigation: NavigationItem[];
  activeNavigation: string;
  defaultNavigation: string;
  onNavigate: (id: string) => void;
}) {
  const resolveCurrentSection = useCallback(
    (state: unknown) => historySection(state, scope, navigation, defaultNavigation),
    [defaultNavigation, navigation, scope],
  );

  useEffect(() => {
    const synchronize = (state: unknown) => {
      const nextSection = resolveCurrentSection(state);
      if (nextSection !== activeNavigation) onNavigate(nextSection);
    };
    const handlePopState = (event: PopStateEvent) => synchronize(event.state);

    synchronize(window.history.state);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [activeNavigation, onNavigate, resolveCurrentSection]);

  return useCallback((id: string) => {
    if (id === activeNavigation || !navigation.some((item) => item.id === id)) return;
    const currentState = window.history.state;
    const baseState = currentState && typeof currentState === "object" ? currentState : {};
    window.history.pushState({
      ...baseState,
      rpcontrolNavigation: { scope, section: id },
    }, "");
    onNavigate(id);
  }, [activeNavigation, navigation, onNavigate, scope]);
}
